import { useAuth } from '../contexts/AuthContext';
import { useCanWriteCompanySettings } from './useCanWriteCompanySettings';

/**
 * Which /expenses controls this user may be shown.
 *
 * ONE FLAG PER API GATE, NOT ONE RULE FOR THE SCREEN (the fe#691 pattern).
 * The three writes on this page are gated three different ways, and reading
 * them off one another is how a control appears that the API refuses. Read
 * from fieldiq-api `expenses.routes.ts`:
 *
 *   reading            GET /expenses — NO role gate at all. Every
 *                      authenticated caller may list; the handler narrows
 *                      instead of refusing (`canSeeAll(user)` → the whole
 *                      company, otherwise `e.user_id = <their own>`). So a
 *                      403 on the list is not something this screen can
 *                      normally produce, and nothing here hides rows.
 *
 *   canApprove         POST /expenses/:id/approve —
 *                      `requireRole('admin', 'office_staff')`, NON-strict,
 *                      so an elevated dispatcher passes.
 *   canReject          POST /expenses/:id/reject — the same gate, kept as its
 *                      own field: they are two routes, and one moving must
 *                      not silently move the other.
 *
 *   canCorrectPaidBy   PATCH /expenses/:id — NO role gate. Authorization is
 *                      `loadVisible()` (someone else's row is a 404) plus a
 *                      `status = 'pending'` gate (409 otherwise). On THIS
 *                      screen every row belongs to someone else, so what
 *                      decides it is whether the reader is one of the office
 *                      view roles the list widens for.
 *
 * THE DISPATCHER IS THE CASE THAT MAKES THIS WORTH A HOOK. A plain
 * `dispatcher` — one whose company has `dispatcher_admin_access` off — is an
 * office VIEW role, so they load every receipt in the tenant and may correct
 * who paid, and they are NOT in the approve/reject list, so both of those
 * come back 403. One rule for the screen gets that wrong in whichever
 * direction it is written.
 *
 * Not `isAdminRole`: it admits `owner`, which neither route's list contains.
 */

/** Roles GET /expenses widens to the whole company for — OFFICE_VIEW_ROLES. */
const OFFICE_VIEW_ROLES = ['admin', 'office_staff', 'dispatcher'];

export interface ExpensePermissions {
  canApprove: boolean;
  canReject: boolean;
  canCorrectPaidBy: boolean;
  /**
   * POST/DELETE /expenses/:id/reimbursed (api#1024) —
   * `requireRole('admin', 'office_staff')`, non-strict: approve's gate exactly,
   * kept as its own field because it is its own route.
   */
  canMarkReimbursed: boolean;
}

export function useExpensePermissions(): ExpensePermissions {
  const { user } = useAuth();
  const role = user?.role;
  const adminOrElevated = useCanWriteCompanySettings();
  const adminOrOffice = adminOrElevated || role === 'office_staff';
  const officeView = typeof role === 'string' && OFFICE_VIEW_ROLES.includes(role);

  return {
    canApprove: adminOrOffice,
    canReject: adminOrOffice,
    canCorrectPaidBy: officeView,
    canMarkReimbursed: adminOrOffice,
  };
}
